import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import * as Mui from "@mui/material";
import type { WithStyles } from "type/tss";
import { useAppThunkDispatch } from "redux/store";
import { Agent } from "model/agent";
import { updateAgent } from "redux/slice/agents";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { Switch } from "component/shared/switch";
import { styles } from "./styles";
import { TabHandle } from "./tab-handle";

interface Props extends WithStyles<typeof styles> {
	agent: Agent;
	onDirtyChange: () => void;
	/**
	 * Self-view renders this as the "Emails" tab, which also carries the listing-alert toggle that
	 * lives on the Access tab in the member-detail view (MAX-4139).
	 */
	selfView?: boolean;
	/** Single-agent accounts have no brokerage branding, so there is no header to sit beneath. */
	isSingleAgent?: boolean;
}

export const EmailHeaderTab = forwardRef<TabHandle, Props>(({ agent, selfView = false, isSingleAgent = false, classes = {}, onDirtyChange }, ref) => {
	const dispatch = useAppThunkDispatch();
	const [emailHeader, setEmailHeader] = useState(agent.emailHeader || "");
	// In self-view this tab is "Emails" and owns the listing-alert toggle, which sits on the Access
	// tab in the member-detail view (MAX-4139).
	const [listingAlertEmails, setListingAlertEmails] = useState(agent.sendEmailUpdatesToAgent || false);
	// Bumped to remount the editor on Cancel — Quill won't re-sync from a changed value prop
	// once it has mounted, so resetting state alone would leave the old content on screen.
	const [editorKey, setEditorKey] = useState(0);
	// False until the editor's mount-time onChange echo has been swallowed (see `update`).
	const editorReady = useRef(false);

	const isDirty = () =>
		emailHeader !== (agent.emailHeader || "") ||
		(selfView && listingAlertEmails !== (agent.sendEmailUpdatesToAgent || false));

	// Notify from an effect rather than inside the change handler: the parent reads isDirty() during
	// render, so telling it synchronously has it look at state React has not flushed yet.
	useEffect(() => { onDirtyChange(); }, [onDirtyChange, listingAlertEmails, emailHeader]);

	useImperativeHandle(ref, () => ({
		isDirty,
		cancel: () => {
			setEmailHeader(agent.emailHeader || "");
			setListingAlertEmails(agent.sendEmailUpdatesToAgent || false);
			// The remount below replays the mount echo, so arm the guard again.
			editorReady.current = false;
			setEditorKey((k) => k + 1);
		},
		save: async () => {
			// .unwrap() so an API failure rejects here instead of resolving into a false success.
			await dispatch(updateAgent({
				agent: selfView
					? { ...agent, emailHeader, sendEmailUpdatesToAgent: listingAlertEmails }
					: { ...agent, emailHeader },
			})).unwrap();
		},
	}));

	// RichTextEditor echoes its value back through onChange once on mount, and Quill may normalize
	// the stored HTML while doing so. Swallowing that first call keeps an untouched tab from
	// reporting dirty — which would otherwise show the save bar and trip UnsavedChangesPrompt.
	const update = (v: string) => {
		if (!editorReady.current) {
			editorReady.current = true;
			return;
		}
		setEmailHeader(v);
	};

	return (
		<div>
			{selfView && (
				<div className={classes.section}>
					<Mui.Typography className={classes.sectionTitle}>Listing Alert Emails</Mui.Typography>
					<Switch
						checked={listingAlertEmails}
						label="Receive copies of listing alert emails sent to your leads"
						description="When enabled, you will receive a copy of every listing alert email sent to your leads. Listing alerts notify leads of new listings that match their saved searches."
						onChange={setListingAlertEmails}
					/>
				</div>
			)}

			{!isSingleAgent && (
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Email Header</Mui.Typography>
			<Mui.Typography className={classes.sectionDescription}>
				Customize the header that appears on all emails sent to your leads. Your header will display below the brokerage branding and above the email content. HTML is supported. If left blank, a default contact message will appear.
			</Mui.Typography>
				<RichTextEditor
					key={editorKey}
					onChange={update}
					toolbarId="profileEmailHeader"
					value={emailHeader}
				/>
			</div>
			)}
		</div>
	);
});
